import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { CircuitBoard } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuthStore();

  useEffect(() => {
    if (user) {
      console.log('User already logged in, redirecting...');
      if (user.role === 'admin' || user.role === 'client') {
        navigate('/cybie');
      } else if (user.role === 'emh') {
        navigate('/emh');
      } else {
        navigate('/cybie');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setDebugInfo('');
    setIsLoading(true);

    console.log('Login attempt:', { email });

    try {
      setDebugInfo('Authenticating...');
      await signIn(email, password);

      console.log('Sign in successful');
      setDebugInfo('Login successful! Redirecting...');

      // Get the user immediately after sign in
      const currentUser = useAuthStore.getState().user;
      console.log('Current user after sign in:', currentUser);

      if (currentUser) {
        // Direct navigation based on role
        if (currentUser.role === 'admin' || currentUser.role === 'client') {
          console.log('Navigating to /cybie');
          navigate('/cybie', { replace: true });
        } else if (currentUser.role === 'emh') {
          console.log('Navigating to /emh');
          navigate('/emh', { replace: true });
        } else {
          console.log('Navigating to /cybie (fallback)');
          navigate('/cybie', { replace: true });
        }
      } else {
        // If user not set yet, wait a bit and try again
        console.log('User not set yet, waiting...');
        await new Promise(resolve => setTimeout(resolve, 500));
        const retryUser = useAuthStore.getState().user;
        if (retryUser) {
          if (retryUser.role === 'admin' || retryUser.role === 'client') {
            navigate('/cybie', { replace: true });
          } else if (retryUser.role === 'emh') {
            navigate('/emh', { replace: true });
          } else {
            navigate('/cybie', { replace: true });
          }
        } else {
          throw new Error('Failed to load user profile after login');
        }
      }
    } catch (err: any) {
      console.error('Login failed:', err);
      let errorMessage = err.message || 'Failed to sign in';

      if (errorMessage.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (errorMessage.includes('Email not confirmed')) {
        errorMessage = 'Please confirm your email address before signing in. Check your inbox for the confirmation link.';
      } else if (errorMessage.includes('User not found')) {
        errorMessage = 'No account found with this email. Please check your email or create a new account.';
      }

      setError(errorMessage);
      setDebugInfo('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <CircuitBoard className="mx-auto h-12 w-12 text-blue-500" />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
              create a new account
            </Link>
          </p>
        </div>

        {location.state?.message && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
            {location.state.message}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
              {error}
            </div>
          )}

          {debugInfo && !error && (
            <div className="bg-blue-50 border border-blue-200 text-blue-600 px-4 py-3 rounded-md">
              {debugInfo}
            </div>
          )}

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                isLoading 
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}